/**
 * UI context registry (klient) — publikuje "kde uživatel v UI stojí" do backendu
 * (ui.context_set), aby si to přečetl chat agent i externí IDE pod stejnou entitou
 * (ui.context_get). Filip 2026-07-10.
 *
 * Merge-and-publish: komponenty volají setUiContext({ open_file: ... }) a jen
 * přepíšou svoje pole; publish je debounced (600 ms), ať to nespamuje.
 */
import { api } from './api';

export interface UiContext {
  route?: string;
  page?: string;
  tab?: string;
  project?: string;
  /** Cesta souboru otevřená v Code editoru (repo-root relative). */
  open_file?: string | null;
  /** true = editor má neuložené změny oproti poslednímu load/save. */
  open_file_dirty?: boolean;
  open_file_line?: number | null;
  open_file_column?: number | null;
  selection?: Record<string, unknown> | null;
  [k: string]: unknown;
}

let current: UiContext = {};
let timer: ReturnType<typeof setTimeout> | null = null;

function publish() {
  timer = null;
  // MCP-first; selhání je best-effort (registr je efemérní metadata)
  api.mcp('ui.context_set', { context: current }).catch(() => {});
}

/** Sloučí částečný kontext do aktuálního a naplánuje debounced publish. */
export function setUiContext(partial: UiContext) {
  let changed = false;
  for (const [k, v] of Object.entries(partial)) {
    if (JSON.stringify(current[k]) !== JSON.stringify(v)) {
      current[k] = v;
      changed = true;
    }
  }
  if (!changed) return;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('acm-ui-context', { detail: current }));
  }
  if (timer) clearTimeout(timer);
  timer = setTimeout(publish, 600);
}

/** Vyčistí pole (např. při opuštění workspace) — nastaví na undefined + publish. */
export function clearUiContext(keys: string[]) {
  setUiContext(Object.fromEntries(keys.map((k) => [k, undefined])));
}

export function getUiContextSnapshot(): UiContext {
  return { ...current };
}
